import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import {find} from '../../../../lib/objectHelper';
import withIntl from '../../../../lib/withIntl';

import WizardHeader from '../../../../components/WizardHeader/WizardHeader';
import {
  ProtocolEditorViewMode,
  Feedback,
  NormalizationStep,
  NormalizationPreviewWellStatus,
  NormalizationType,
  TrayType,
} from '../../../../constants/dataEnum';

import {showSnackbar} from '../../../../stores/actions/appStateAction';
import {showModal, hideModal} from '../../../../stores/actions/modalStateAction';
import {setAllTaskIsSelected} from '../../../../stores/actions/cardStateAction';
import {cancelHandler} from '../../../../stores/business/taskListNormalizationBusinessAction';
import {globalAddUndoItem} from '../../../../stores/business/undoRedoBusinessAction';
import {setNormalizationTaskEditStep, setSelectedMenu} from '../../../../stores/actions/protocolEditorAction';

const mapStateToProps = (state) => {
  const {normalizationTaskEditStep} = state.protocolEditorState;
  const {currentWizardView} = state.normalizationState;
  const {minAspirateHighVolume} = state.configState;
  const {lastOpenedProtocol} = state.protocolState;

  return {
    cardStateCards: state.cardState.cards,
    cardStateTasks: state.cardState.tasks,
    normalizationState: state.normalizationState,
    protocolNormalizationTaskEditStep: normalizationTaskEditStep,
    normalizationStateNormalizationStep: currentWizardView,
    minAspirateHighVolume: minAspirateHighVolume.value,
    lastOpenedProtocol,
  };
};

const mapDispatchToProps = (dispatch) => ({
  appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  modalStateActionShowModal: bindActionCreators(showModal, dispatch),
  modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
  taskListNormalizationBusinessCancelHandler: bindActionCreators(cancelHandler, dispatch),
  cardStateActionSetAllTaskIsSelected: bindActionCreators(setAllTaskIsSelected, dispatch),
  undoRedoBusinessActionGlobalAddUndoItem: bindActionCreators(globalAddUndoItem, dispatch),
  protocolEditorStateActionSetNormalizationTaskEditStep: bindActionCreators(setNormalizationTaskEditStep, dispatch),
  setProtocolEditorMode: bindActionCreators(setSelectedMenu, dispatch),
});

function NormalizationHeader({
  intl,
  normalizationState,
  appStateActionShowSnackbar,
  modalStateActionShowModal,
  modalStateActionHideModal,
  taskListNormalizationBusinessCancelHandler,
  notifyBack,
  notifyNext,
  notifyCancel,
  notifyCancelEdit,
  headerRef,
  cardStateActionSetAllTaskIsSelected,
  undoRedoBusinessActionGlobalAddUndoItem,
  protocolNormalizationTaskEditStep,
  protocolEditorStateActionSetNormalizationTaskEditStep,
  normalizationStateNormalizationStep,
  setProtocolEditorMode,
  minAspirateHighVolume,
  lastOpenedProtocol,
}) {
  const isEdit = !!protocolNormalizationTaskEditStep;
  const step = normalizationStateNormalizationStep;
  const {type: normalizationType} = normalizationState;

  const hideBackButton = () => {
    if (isEdit) {
      return (
        step === NormalizationStep.UPLOAD_AND_MANAGE_CSV_FILE ||
        step === NormalizationStep.SET_DILUENT_LABWARE ||
        step === NormalizationStep.UPLOAD_AND_MANAGE_CSV_FILE ||
        step === NormalizationStep.SET_SAMPLE_LABWARE
      );
    }
    return step === NormalizationStep.SET_SAMPLE_LABWARE;
  };

  const hideNextButton = () => {
    if (isEdit) {
      return step === NormalizationStep.SET_SAMPLE_LABWARE || step === NormalizationStep.SET_DILUENT_LABWARE;
    }
    return false;
  };
  const disableNextButton = () => {
    const {targetConcentration, selectedColumnIndex, previewData, diluentLabwareID, sampleLabwareID} =
      normalizationState;

    let isDisabled;
    switch (step) {
      case NormalizationStep.SET_SAMPLE_LABWARE:
        isDisabled = !sampleLabwareID;
        break;
      case NormalizationStep.SET_DILUENT_LABWARE:
        isDisabled = !diluentLabwareID;
        break;
      case NormalizationStep.UPLOAD_AND_MANAGE_CSV_FILE:
        isDisabled = selectedColumnIndex === null;
        break;
      case NormalizationStep.SET_TARGET_CONCENTRATION: {
        const isInvalid = find(previewData, {status: NormalizationPreviewWellStatus.INVALID});
        const hasVolumeBelowMinimum = previewData.some(
          (data) => data.volume < minAspirateHighVolume && data.volume !== 0,
        );
        const skipped = previewData.filter((data) => data.status === NormalizationPreviewWellStatus.SKIPPED);

        isDisabled =
          parseFloat(targetConcentration) === 0 ||
          !!isInvalid ||
          skipped.length === previewData.length ||
          (hasVolumeBelowMinimum && lastOpenedProtocol.trayType === TrayType.HIGH_VOLUME_CADDY);
        break;
      }
      default:
        break;
    }
    return isDisabled;
  };

  const onBack = () => {
    notifyBack();
  };

  const onNext = () => {
    notifyNext();
  };

  const onCancel = () => {
    if (isEdit) {
      setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);

      if (isEdit) {
        notifyCancelEdit();
      } else {
        notifyCancel();
        cardStateActionSetAllTaskIsSelected(false);
      }
      undoRedoBusinessActionGlobalAddUndoItem();
      return;
    }

    const title = intl.formatMessage({id: 'protocolEditor.normalization.cancelModal.title'});
    const text = intl.formatMessage({id: 'protocolEditor.normalization.cancelModal.text'});
    const okButtonText = intl.formatMessage({id: 'protocolEditor.normalization.cancelModal.okButton'});
    const cancelButtonText = intl.formatMessage({id: 'protocolEditor.normalization.cancelModal.cancelButton'});
    const cancelSuccessMessage = intl.formatMessage({id: 'protocolEditor.normalization.cancelModal.successMessage'});

    const modalOkHandler = (isButton = true) => {
      taskListNormalizationBusinessCancelHandler();
      setProtocolEditorMode(ProtocolEditorViewMode.SIMPLE_TRANSFER);
      protocolEditorStateActionSetNormalizationTaskEditStep(null);
      if (isButton) modalStateActionHideModal();
      appStateActionShowSnackbar(Feedback.SUCCESS, cancelSuccessMessage);
      notifyCancel();
      cardStateActionSetAllTaskIsSelected(false);
      undoRedoBusinessActionGlobalAddUndoItem();
    };

    const modalCancelHandler = () => {
      setProtocolEditorMode(ProtocolEditorViewMode.NORMALIZATION);
      modalStateActionHideModal();
    };

    if (step === NormalizationStep.SET_SAMPLE_LABWARE) {
      modalOkHandler(false);
      return;
    }

    modalStateActionShowModal(
      title,
      text,
      [
        {className: 'btn-lg', style: 'primary', handlerClick: modalOkHandler, text: okButtonText},
        {className: 'btn-lg', style: 'secondary', handlerClick: modalCancelHandler, text: cancelButtonText},
      ],
      modalCancelHandler,
    );
  };

  const renderText = () => {
    // const { csvData, selectedColumnIndex } = normalizationState;

    let text = '';

    switch (step) {
      case 1:
      // eslint-disable-next-line default-case-last, no-fallthrough
      default:
        text =
          normalizationType === NormalizationType.FIXED_SAMPLE_VOLUME
            ? 'Step 1. Select your sample plate and the location of the normalization.'
            : 'Step 1. Select your sample plate.';
        break;
      case 2:
        text =
          normalizationType === NormalizationType.FIXED_DILUENT_VOLUME
            ? 'Step 2. Choose your diluent and the location of the normalization.'
            : 'Step 2. Choose your diluent plate.';
        break;
      case 3: {
        text = 'Step 3: Upload the starting concentration and define the data for normalization.';
        break;
      }
      case 4:
        text = 'Step 4. Normalization Settings ';
        break;
      case 5:
        text = 'Step 5. Select Diluent Source';
        break;
    }

    return (
      <span className="header-text">
        <span className="header-text-title">{text}</span>
      </span>
    );
  };

  const nextButtonText = () => {
    let text = 'NEXT';

    if (isEdit && step === NormalizationStep.SET_TARGET_CONCENTRATION) {
      text = 'APPLY';
    } else if (step === NormalizationStep.SET_TARGET_CONCENTRATION) {
      text = 'FINISH';
    }

    return text;
  };

  return (
    <div ref={headerRef}>
      <WizardHeader
        hideBackButton={hideBackButton()}
        hideNextButton={hideNextButton()}
        disableNextButton={disableNextButton()}
        nextButtonText={nextButtonText()}
        cancelButtonText="CANCEL"
        onBackClick={onBack}
        onNextClick={onNext}
        onCancelClick={onCancel}
      >
        {renderText()}
      </WizardHeader>
    </div>
  );
}

NormalizationHeader.propTypes = {
  intl: PropTypes.object.isRequired,
  normalizationState: PropTypes.object.isRequired,
  appStateActionShowSnackbar: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  notifyBack: PropTypes.func,
  notifyNext: PropTypes.func,
  notifyCancel: PropTypes.func,
  notifyCancelEdit: PropTypes.func,
  headerRef: PropTypes.any,
  cardStateActionSetAllTaskIsSelected: PropTypes.func.isRequired,
  undoRedoBusinessActionGlobalAddUndoItem: PropTypes.func.isRequired,
  protocolNormalizationTaskEditStep: PropTypes.any,
  protocolEditorStateActionSetNormalizationTaskEditStep: PropTypes.func.isRequired,
  normalizationStateNormalizationStep: PropTypes.number,
  taskListNormalizationBusinessCancelHandler: PropTypes.func,
  setProtocolEditorMode: PropTypes.func.isRequired,
  minAspirateHighVolume: PropTypes.number.isRequired,
  lastOpenedProtocol: PropTypes.object.isRequired,
};

NormalizationHeader.defaultProps = {
  notifyBack: () => {},
  notifyNext: () => {},
  notifyCancel: () => {},
  notifyCancelEdit: () => {},
  headerRef: null,
  taskListNormalizationBusinessCancelHandler: () => {},
  normalizationStateNormalizationStep: null,
  protocolNormalizationTaskEditStep: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(NormalizationHeader));
